import { createSlice, createAction, PayloadAction, createSelector } from '@reduxjs/toolkit'
import { getLocalStorageItem, setLocalStorageItem } from 'app/api/localStorage'
import { RootState } from 'app/state/store'
import { RequestStatus } from 'features/common'
import {
  CARTO_IGNORED_PROCESSING_JOBS,
  ImportingJob,
  ImportingActionPayload,
  JobStatusType,
  ImportingJobPayload
} from '../types'
import { isPendingOrRunning } from '../models/jobs'

export const name = 'importingJobs'
export const CREATE_IMPORTING_JOB_REQUEST = `${name}/create_importing_job_request`
export const GET_IMPORTING_JOBS_REQUEST = `${name}/get_importing_jobs_request`

export interface JobsImportingState {
  importingJobs: Array<ImportingJob>
  importingJobsCleared: Array<ImportingJob>
  importingJobsStatusRequest: RequestStatus
  currentImportingJob?: ImportingJob
}

const initialState: JobsImportingState = {
  importingJobs: [],
  importingJobsCleared: getLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS) || [],
  importingJobsStatusRequest: RequestStatus.Idle
}

const asyncActions = {
  createImportingJobRequest: createAction<ImportingActionPayload>(CREATE_IMPORTING_JOB_REQUEST),
  getImportingJobsRequest: createAction<ImportingJobPayload>(GET_IMPORTING_JOBS_REQUEST)
}

const reducers = {
  setCurrentImportingJob: (state: JobsImportingState, action: PayloadAction<ImportingJob | undefined>) => {
    state.currentImportingJob = action.payload
  },
  setImportingJobsStatusRequest: (state: JobsImportingState, action: PayloadAction<RequestStatus>) => {
    state.importingJobsStatusRequest = action.payload
  },
  setImportingJobs: (state: JobsImportingState, action: PayloadAction<Array<ImportingJob>>) => {
    state.importingJobs = action.payload
  },
  updateImportingJobStatus: (state: JobsImportingState, action: PayloadAction<ImportingJob>) => {
    const importingJobs = [...state.importingJobs]
    const { status, jobId, error } = action.payload

    importingJobs.forEach((job) => {
      if (job.jobId === jobId) {
        job.status = status
        job.error = error
      }
    })

    state.importingJobs = importingJobs
  },
  addImportingJob: (state: JobsImportingState, action: PayloadAction<ImportingJob>) => {
    const newJob = action.payload
    const importingJobs = state.importingJobs.filter((job) => job.jobId !== newJob.jobId)
    importingJobs.unshift(newJob)
    state.importingJobs = importingJobs
  },
  removeImportingJob: (state: JobsImportingState, action: PayloadAction<ImportingJob['jobId']>) => {
    const importingJobs = state.importingJobs.filter((job) => job.jobId !== action.payload)
    state.importingJobs = importingJobs

    if (state.currentImportingJob?.jobId === action.payload) {
      state.currentImportingJob = undefined
    }
  },
  setImportingJobsCleared: (state: JobsImportingState) => {
    state.importingJobsCleared = state.importingJobs.filter(
      (job) => job.status !== JobStatusType.Pending && job.status !== JobStatusType.Running
    )
    setLocalStorageItem(CARTO_IGNORED_PROCESSING_JOBS, state.importingJobsCleared)
  }
}

export const areImportingJobsPending = createSelector(
  (state: RootState) => state.jobsImporting.importingJobs,
  (importingJobs) => importingJobs.some(isPendingOrRunning)
)

export const getImportingJobsNotCleared = createSelector(
  [
    (state: RootState) => state.jobsImporting.importingJobs,
    (state: RootState) => state.jobsImporting.importingJobsCleared
  ],
  (importingJobs, importingJobsCleared) => {
    return importingJobs.filter((job) => {
      return !importingJobsCleared.find((JobCleared) => JobCleared.jobId === job.jobId)
    })
  }
)

export const JobsImportingSlice = createSlice({
  name,
  initialState,
  reducers
})

export const {
  setCurrentImportingJob,
  setImportingJobsStatusRequest,
  setImportingJobs,
  updateImportingJobStatus,
  addImportingJob,
  removeImportingJob,
  setImportingJobsCleared
} = JobsImportingSlice.actions

export const { createImportingJobRequest, getImportingJobsRequest } = asyncActions

export default JobsImportingSlice.reducer
